import React, { useRef } from "react";
import { styled } from "@mui/material/styles";
import { Box, Link, Typography } from "@mui/material";
const PREFIX = "Overview";

const classes = {
  ul: `${PREFIX}-ul`,
  index: `${PREFIX}-index`,
  listItem: `${PREFIX}-listItem`,
  section: `${PREFIX}-section`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.ul}`]: { marginTop: 0 },

  [`& .${classes.index}`]: {
    margin: 0,
    padding: 0,
    cursor: "pointer",
  },

  [`& .${classes.listItem}`]: {
    margin: 0,
    padding: 0,
  },

  [`& .${classes.section}`]: {
    marginTop: 25,
  },
}));

const handleScroll = (ref: any) => {
  const y = ref.getBoundingClientRect().top + window.pageYOffset - 100;

  window.scrollTo({ top: y, behavior: "smooth" });
};

export default function SODOverview({ title }: { title?: React.JSX.Element }) {
  const SODFeature = useRef(null);
  const Disclaimer = useRef(null);
  const Survey = useRef(null);
  const Explanatory = useRef(null);
  const Contact = useRef(null);
  const Return_To_Index = useRef(null);

  const returnLink = (
    <Typography
      variant="body2"
      style={{ textAlign: "right", paddingBottom: 16, cursor: "pointer" }}
    >
      <Link
        underline="always"
        onClick={() => {
          handleScroll(Return_To_Index.current);
        }}
      >
        Return To Index
      </Link>
    </Typography>
  );

  const refLink = (ref: any, name: string) => {
    const id = `${name.toLowerCase()}Link`;
    return (
      <Link
        id={id}
        underline="always"
        onClick={() => {
          handleScroll(ref.current);
        }}
      >
        {name}
      </Link>
    );
  };

  return (
    <Root>
      <div ref={Return_To_Index}></div>
      {title}
      <Typography variant="h3" style={{ paddingBottom: 0, marginTop: 5 }}>
        Index of Contents
      </Typography>
      <ul style={{ marginTop: 0, marginBottom: 50 }}>
        <li>
          <Typography variant="body2" className={classes.index}>
            {refLink(SODFeature, "SOD Features & Functionality")}
          </Typography>
        </li>
        <li>
          <Typography variant="body2" className={classes.index}>
            {refLink(Disclaimer, "Disclaimer")}
          </Typography>
        </li>{" "}
        <li>
          <Typography variant="body2" className={classes.index}>
            {refLink(Survey, "Survey Forms and Instructions")}
          </Typography>
        </li>{" "}
        <li>
          <Typography variant="body2" className={classes.index}>
            {refLink(Explanatory, "Explanatory Notes")}
          </Typography>
        </li>{" "}
        <li>
          <Typography variant="body2" className={classes.index}>
            {refLink(Contact, "Contact Information")}
          </Typography>
        </li>
      </ul>
      <Box
        ref={SODFeature}
        id="sod_features_functionality"
        className={classes.section}
      >
        <Typography variant="h2" gutterBottom>
          SOD Features & Functionality
        </Typography>
        <Typography variant="h3">Branch Offices and Deposits</Typography>
        <Typography
          variant="body2"
          color="primary.main"
          className={classes.listItem}
        >
          Search the SOD by holding company, institution and/or geographic area.
          All options result in a listing of branch offices by state, county and
          institution:
        </Typography>
        <ul>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <b>Institution</b> - enter the name, FDIC certificate number,
              RSSD, OCC charter number or OTS docket number to get a listing of
              branch offices for that institution.
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <b>Bank Holding Company</b> - entering the name or RSSDHCR number
              of the holding company will return a listing of branch offices by
              state, county and subsidiary.
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <b>State / County / City / ZIP</b> - will allow the user to get a
              locational targeted listing of branches by state, county, city
              and/or ZIP code.
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <b>Metropolitan Statistical Areas (MSA)</b> - will result in a
              listing of all branches located within the selected MSA. The
              Metropolitan Statistical Areas (MSA's) are based on the 2020
              Census. These areas correspond to the state/county/CBSA
              relationship as defined by the Census Bureau. For the purpose of
              the Summary of Deposit, MSA's reflect only the CBSA codes for
              metropolitan areas. All of the MSA related fields are available in
              the SOD database and download.
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <b>ZIP Code</b> - this provides a shortcut for the user to get a
              listing of branches for a specific ZIP code. Please note that ZIP
              codes do have limitations and can change from year to year. The
              reported ZIP code also defines the USPS postal city. Therefore, if
              the reported ZIP code is in error, the postal city may be
              incorrect.
            </Typography>
          </li>
        </ul>
        <Typography
          variant="h3"
          sx={{
            marginTop: 3,
          }}
        >
          Deposit Market Share
        </Typography>
        <Typography variant="body2" color="primary.main">
          The Deposit Market Share is the percentage of deposits an FDIC-insured
          institution has within a defined geographic market. This data is based
          on the annual Summary of Deposits (SOD) survey for FDIC-insured
          institutions as of June 30.
        </Typography>
        <Typography variant="body2" color="primary.main">
          The Deposit Market Share and the Pro Forma (HHI) Reports provide
          information for all institutions within a specific geographic market
          for a specific time period. The Market Presence and Growth Rate
          Reports provide similar information, but from the perspective of one
          institution. All reports provide data back to 1994 and are available
          by institution or bank holding company.
        </Typography>
        <ul>
          <li>
            <Typography variant="body2" color="primary.main">
              The <b>Deposit Market Share Report</b> is a summary of all
              FDIC-insured institutions or bank holding companies, the number of
              branches, the amount of deposits and the percentage of their
              market share for any specified geographic area (state, county,
              city, zip code). Multiple geographic components can be combined to
              create user defined custom markets (e.g. multiple counties, zip
              codes, cities or combinations) by holding down the Control Key
              while selecting within the list box.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The <b>Pro Forma HHI Report </b> offers the same information as
              the Deposit Market Share Report plus the ability to see the before
              and after effects of proposed mergers or acquisitions within the
              specified geographic market. After selecting the geographic
              market, the user must select the acquiring institution as well as
              the divested institution or branches of an institution (Merged) in
              order to get the results. The results include the before ("Old
              Institution") and after (“New Institution”) data with the summary
              of all FDIC-insured institutions within the geographic area as
              well as the number of branches, the amount of deposits, the market
              share and HHI. Multiple geographic components can be combined to
              create user defined custom markets (e.g. multiple states,
              counties, cities, and ZIP codes).
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The <b>Market Presence Report</b> provides deposit market share
              data for any one FDIC-insured institution or bank holding company
              within every state, county or MSA in one report.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The <b>Growth Rate Report </b>provides the ability to compare two
              years of data for any FDIC-insured institution or bank holding
              company by state, county or MSA.
            </Typography>
          </li>
        </ul>
        <Typography
          variant="h3"
          sx={{
            marginTop: 3,
          }}
        >
          Deposit Summary Tables
        </Typography>
        <Typography
          variant="body2"
          color="primary.main"
          className={classes.listItem}
        >
          Provide deposit data by selected aggregations:
        </Typography>
        <ul>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              National Summary Tables
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              Regional Summary Tables
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              Individual State Tables by
            </Typography>
          </li>
          <ul>
            <li>
              <Typography
                variant="body2"
                color="primary.main"
                className={classes.listItem}
              >
                County
              </Typography>
            </li>
            <li>
              <Typography
                variant="body2"
                color="primary.main"
                className={classes.listItem}
              >
                Charter class
              </Typography>
            </li>
          </ul>
        </ul>
        <Typography
          variant="h3"
          sx={{
            marginTop: 3,
          }}
        >
          Data Download
        </Typography>
        <Typography variant="body2" color="primary.main">
          BFS provides many options for downloading SOD data:
        </Typography>
        <ul>
          <li>
            <Typography variant="body2" color="primary.main">
              Download the immediate results of any report you create. Use the
              download button next to the results table you wish to
              download.{" "}
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The SOD Custom Data download allows you to create data sets
              customized by specific variables and location. You can choose the
              variables, split into three groups by Branch/Institution/Bank Hold
              Company, as well as create tailored regional markets. You can save
              your customized download settings to use again in the future.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The Bulk Data Generator allows you to download the entire SOD data
              set for any given year.
            </Typography>
          </li>
        </ul>
        {returnLink}
      </Box>
      <Box ref={Disclaimer} id="disclaimer" className={classes.section}>
        <Typography variant="h2" gutterBottom>
          Disclaimer
        </Typography>
        <Typography variant="body2" color="primary.main">
          The Summary of Deposits data is presented "as reported" by the
          FDIC-insured institutions that have submitted the annual survey.
          Although every attempt has been made to ensure the reliability of the
          information contained herein, some inaccuracies may exist. For this
          reason, the FDIC and OTS neither assume responsibility for, nor
          certify to the accuracy of any of the reported data. Further, these
          data do not indicate approval or disapproval of any particular
          institution's performance and are not to be construed as a rating of
          any institution by federal regulators. Users are cautioned that any
          conclusions that they may draw from the Summary of Deposits data are
          their own and are not to be attributed to the federal regulators. In
          addition, a determination of competitiveness is only one of several
          assessment factors used by Federal agencies during the application
          review process.
        </Typography>
        <Typography variant="body2" color="primary.main">
          The branch office deposit information is obtained primarily through
          the annual surveys of deposits conducted by the Federal Deposit
          Insurance Corporation (FDIC). Information for unit banks, banks
          without branches, is obtained from the June Reports of Condition and
          Thrift Financial Reports submitted by all FDIC-insured depository
          institutions. These reports are prepared in accordance with federal
          regulatory instructions. Explanatory Notes is an integral part of this
          publication and provides information regarding the source of data and
          their use.
        </Typography>
        {returnLink}
      </Box>
      <Box ref={Survey} id="survery_forms">
        <Typography variant="h2" gutterBottom>
          Survey Forms and Instructions
        </Typography>
        <Typography
          variant="body2"
          color="primary.main"
          className={classes.listItem}
        >
          If you need the actual survey forms and/or instructions for filing
          purposes, please refer to the Financial Institution Letter for the
          Summary of Deposits survey. All Links on this page reference Portable
          Document Format (PDF) files. Adobe Acrobat, a reader available for
          free on the Internet, is required to display or print PDF files.
        </Typography>
        <ul>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              <a href="https://www.fdic.gov/bank-financial-reports/summary-deposits">
                SOD Reporting Instructions
              </a>
            </Typography>
          </li>
        </ul>
        <Typography
          variant="body2"
          color="primary.main"
          className={classes.listItem}
        >
          For questions regarding the survey:
        </Typography>
        <ul>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              Email: <a href="mailto:SOD@fdic.gov">SOD@fdic.gov</a>
            </Typography>
          </li>
          <li>
            <Typography
              variant="body2"
              color="primary.main"
              className={classes.listItem}
            >
              Phone: 1-877-275-3342
            </Typography>
          </li>
        </ul>
        {returnLink}
      </Box>
      <Box ref={Explanatory} id="explanatory_notes" className={classes.section}>
        <Typography variant="h2" gutterBottom>
          Explanatory Notes
        </Typography>
        <Typography variant="h3">Scope of the Surveys</Typography>
        <Typography variant="body2" color="primary.main">
          To provide a means for measuring deposits in local banking markets,
          the surveys obtain deposit figures for each banking office of branch
          banking systems, and for each insured U.S. branch of a foreign bank.
          Deposit figures for unit banks (which do not have branch offices) were
          obtained from the June Reports of Condition.
        </Typography>
        <Typography variant="body2" color="primary.main">
          Deposit Reporting - Institutions should report their deposits in a
          manner consistent with their existing internal record-keeping
          practices, however, other methods that logically reflect the deposit
          gathering activity of the bank's branches may be used. It is
          recognized that certain classes of deposits and deposits of certain
          types of customers may be assigned to a single office. Please refer to
          the reporting instructions for more information (see above).
        </Typography>
        <Typography variant="h3">Arrangement of Published Data</Typography>
        <Typography variant="body2" color="primary.main">
          Because these publications are used as a source of market share
          information for individual banking markets, the figures for each
          geographical area only include deposits of offices located within that
          area. Several institutions have designated offices that do not accept
          deposits; these have been included in the survey to provide a more
          complete listing of all offices.
        </Typography>
        <Typography variant="h3">
          Additional points that should be noted:
        </Typography>
        <ul>
          <li>
            <Typography variant="body2" color="primary.main">
              With the exception noted above, "banking office" is defined to
              include all offices and facilities that actually hold deposits,
              and does not include loan production offices, computer centers,
              and other non-deposit installations, such as automated teller
              machines (ATMs).
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              Institutions are allowed to combine deposit data from two or more
              offices within the same county for the following office types:
              drive-in offices, seasonal branches, and military facilities.
              Where centralized bookkeeping or other conditions make it
              impossible to report exact figures, estimates are required.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              International Banking Facility (IBF) deposits are considered
              deposits in foreign offices and are not included in the Summary of
              Deposits Survey.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              Offices and deposits are reported by the institution that owned
              the office as of the close of business on June 30.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              The term "offices" includes both main offices and branches. An
              institution with four branches operates a total of five offices.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              All reports submitted to the FDIC have been validated and
              corrected to the extent possible. There may be rounding
              differences or minor reporting errors reflected in the tables.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="primary.main">
              Savings Institutions include all FDIC-Insured financial
              institutions that operate under federal or state thrift banking
              charters. Prior to August 9, 1989, all institutions insured by the
              Federal Savings and Loan Insurance Corporation (FSLIC) and all
              savings banks insured by the FDIC are included in any applicable
              chart.
            </Typography>
          </li>
        </ul>
        {returnLink}
      </Box>
      <Box ref={Contact} id="contact_info" className={classes.section}>
        <Typography variant="h2" gutterBottom>
          Contact Information
        </Typography>
        <Typography variant="body2" color="primary.main">
          We rely on your comments to improve our products. If you have any
          suggestions or comments about this product, we would love to hear from
          you. Please send your comments to{" "}
          <a href="https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?language=en_US&app=SOD">
            Questions, Suggestions & Requests
          </a>
          . If possible, include your telephone number.
        </Typography>
        <Typography variant="body2" color="primary.main">
          For questions regarding the submission or collection of the Summary of
          Deposits survey, please email us at{" "}
          <a href="mailto:SOD@fdic.gov">SOD@fdic.gov</a>, or Banker questions
          call toll-free, 1-877-275-3342, Option 5 from the Banker Menu.
        </Typography>
        {returnLink}
      </Box>
    </Root>
  );
}
